import { createTheme } from '@mui/material/styles';
import { color, font, radius, motion, semantic } from './tokens';

/** Custom Button variants exposed to TypeScript (used via `<Button variant="primary">`). */
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    primary: true;
    signal: true;
    ghost: true;
    fire: true;
    muted: true;
  }
}

const baseButton = {
  textTransform: 'none' as const,
  fontWeight: 600,
  borderRadius: radius.md,
  boxShadow: 'none',
  transition: `all 0.18s ${motion.easeOutExpo}`,
  '&:hover': { boxShadow: 'none' },
};

/**
 * The side panel's MUI theme. Its Button variants exist only here, so a view
 * that uses them (AppButton, every card) must render under this theme — on a
 * page with another theme, nest a `PanelThemeProvider`.
 */
const panelTheme = createTheme({
  palette: {
    primary: {
      main: color.cobalt,
      dark: color.cobaltDeep,
      light: color.cobaltLite,
      contrastText: color.white,
    },
    secondary: { main: color.signal, contrastText: color.ink },
    error: { main: color.fire },
    warning: { main: color.gold },
    success: { main: semantic.success },
    background: { default: color.surface, paper: color.white },
    // The original theme set text.primary to white, forcing every component to
    // re-declare a dark colour locally. Ink-on-surface is the correct default.
    text: { primary: color.neutral900, secondary: color.neutral600 },
  },

  shape: { borderRadius: radius.md },

  typography: {
    fontFamily: font.body,
    fontSize: 14,
    h1: { fontFamily: font.display, fontWeight: 400, fontSize: '2rem' },
    h2: { fontFamily: font.display, fontWeight: 400, fontSize: '1.75rem' },
    h3: { fontFamily: font.display, fontWeight: 400, fontSize: '1.5rem' },
    h4: { fontFamily: font.display, fontWeight: 400, fontSize: '1.25rem' },
    h5: { fontFamily: font.display, fontWeight: 400, fontSize: '1.1rem' },
    h6: { fontFamily: font.display, fontWeight: 400, fontSize: '1rem' },
    body1: { fontSize: 14 },
    body2: { fontSize: 14 },
    caption: { fontSize: 11 },
  },

  components: {
    MuiCssBaseline: {
      styleOverrides: {
        '@keyframes dooglie-pulse': {
          '0%, 100%': { boxShadow: `0 0 0 0 ${color.signal}80` },
          '50%': { boxShadow: `0 0 0 6px ${color.signal}00` },
        },
        '@keyframes dooglie-pulse-sync': {
          '0%, 100%': { boxShadow: `0 0 0 0 ${color.cobalt}80` },
          '50%': { boxShadow: `0 0 0 6px ${color.cobalt}00` },
        },
        '*::-webkit-scrollbar': { width: 6, height: 6 },
        '*::-webkit-scrollbar-thumb': {
          background: color.neutral200,
          borderRadius: radius.sm,
        },
      },
    },

    MuiButton: {
      defaultProps: { disableElevation: true, variant: 'primary' },
      styleOverrides: { root: baseButton },
      variants: [
        {
          props: { variant: 'primary' },
          style: {
            background: color.cobalt,
            color: color.white,
            '&:hover': { background: color.cobaltDeep },
            '&.Mui-disabled': { background: color.neutral200, color: color.neutral400 },
          },
        },
        {
          props: { variant: 'signal' },
          style: {
            background: color.signal,
            color: color.ink,
            '&:hover': { background: color.signalDim },
          },
        },
        {
          props: { variant: 'fire' },
          style: {
            background: color.fire,
            color: color.white,
            '&:hover': { background: color.fire, filter: 'brightness(0.94)' },
          },
        },
        {
          props: { variant: 'ghost' },
          style: {
            background: 'transparent',
            color: color.cobalt,
            border: `1.5px solid ${color.cobaltGhost}`,
            '&:hover': { background: color.cobaltGhost },
          },
        },
        {
          props: { variant: 'muted' },
          style: {
            background: color.neutral100,
            color: color.neutral600,
            '&:hover': { background: color.neutral200 },
            '&.Mui-disabled': { background: color.neutral100, color: color.neutral400 },
          },
        },
      ],
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          background: color.white,
          borderRadius: radius.md,
          '& fieldset': { borderColor: color.neutral200, borderWidth: 1.5 },
          '&:hover fieldset': { borderColor: color.cobaltLite },
          '&.Mui-focused fieldset': { borderColor: color.cobalt, borderWidth: 1.5 },
        },
        input: { color: color.neutral900 },
      },
    },

    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          '&:before': { display: 'none' },
        },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: { background: color.ink, fontSize: 12, borderRadius: radius.sm },
        arrow: { color: color.ink },
      },
    },
  },
});

export { panelTheme };
export default panelTheme;
